import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import getMetricSpec from "../../merchandising/utils/metrics";
import { useDSPInventoryDatasetsQuery } from "../hooks/useDSPInventoryDatasetsQuery";

const InventoryExpander = ({ row, lineItemId }) => {
  const { t } = useTranslation(["dsp"]);
  const { group } = useDates();
  const { site } = row.original ?? {};
  const hasImpression = row.original?.impressions;

  const { data: datasetsResult, isLoading: isInventoryDataLoading } = useDSPInventoryDatasetsQuery({
    dimensions: ["site"],
    lineItemId,
    site,
  });

  const { data: datasetsTimeSeriesResult, isLoading: isInventoryTimeSeriesDataLoading } = useDSPInventoryDatasetsQuery({
    dimensions: ["site", group],
    lineItemId,
    site,
  });

  const data = useMemo(() => {
    const timeSeriesRows = datasetsTimeSeriesResult?.data ?? [];
    return {
      date: timeSeriesRows.map((row) => ({ ...row, start_date: row.start_date ?? row.date })),
      total: datasetsResult?.data?.[0] ?? {},
      metadata: {},
      isLoading: isInventoryDataLoading,
    };
  }, [datasetsResult, datasetsTimeSeriesResult, isInventoryDataLoading]);

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["dsp_sales", "dsp_cost", "dsp_units"],
    "dspInventoryExpander",
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("dsp_sales"),
      getMetricSpec("dsp_total_sales"),
      getMetricSpec("dsp_impressions"),
      getMetricSpec("dsp_clicks"),
      getMetricSpec("dsp_cost"),
      getMetricSpec("dsp_total_purchases"),
    ],
    [],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  if (isInventoryDataLoading || isInventoryTimeSeriesDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} md={6} className="pb-4 px-5">
          <h3
            style={{
              fontSize: 22,
            }}
          >
            {site}
          </h3>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="px-5 py-3">
          <>
            {hasImpression ?
              <>
                <Col xs={12}>
                  <MultiMetricChart
                    metrics={metrics}
                    selectedMetrics={selectedMetrics}
                    data={data?.date ?? []}
                    group={group}
                    handleLegendChange={handleLegendChange}
                    showAnnotations={false}
                  />
                </Col>
                <Col xs={12}>
                  <Row className="pt-4">
                    {metrics?.map((m, i) => {
                      return (
                        <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                          <div className="">
                            <div>
                              <DetailedMetric center metric={m} data={data?.total} showTarget={false} />
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              </>
            : <Col xs={12} className="text-center pb-5">
                <h4>{t(($) => $.dsp["No Data Available"])}</h4>
                <p>{t(($) => $.dsp["This section will be populated once the site has at least 1 impression."])}</p>
              </Col>
            }
          </>
        </Col>
      </Row>
    </div>
  );
};

export default InventoryExpander;
